import React from 'react';
import Immutable from 'immutable';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  Container,
  WysiwygField,
  useWindowDimensions,
} from '@uncinc/uncinc-react-kitchen-sink';

import {
  Paragraph,
  Block,
  DynamicMediaItem,
} from '@uncinc/react-drupal-core';
import {
  ImmutableEntity,
  ImmutableRootState,
} from '@uncinc/react-drupal-core/types';

import {
  getOverviewUrlFromMenu,
} from '../../../../helpers';

import { NodeArticle } from '../../../../types/uncinc_profile/nodes';
import ImageLightbox from '../../../../components/ImageLightbox';
import BackButton from '../../../../components/BackButton';
import ShareWrapper from '../../../../components/ShareWrapper';
import ArticleMetadata from '../../../../components/ArticleMetadata';
import ThemesLabels from '../../../../components/ThemesLabels';
import {
  SCREEN_TABLET, BLOCK_RELATED_ARTICLES_BLOCK_ID,
} from '../../../../config/constants';

import './style.scss';


interface NodeArticlePageProps extends React.ComponentPropsWithoutRef<'div'> {
  entity: ImmutableEntity<NodeArticle>;
}

const NodeArticlePage: React.FC<NodeArticlePageProps> = ({ entity }) => {
  const { t } = useTranslation();
  const article = entity.toJS();
  const title = article.title[0].value;
  const articleType = article.field_article_type[0].entity.name[0].value;
  const articleTypeTargetId = article.field_article_type[0]?.target_id;
  const imageCaption = article.field_media_item[0]?.entity.field_caption[0]?.value;
  const paragraphs = article.field_paragraphs;
  const labels = article.field_labels;
  const labelsIds = labels.map((label) => label.target_id);
  const body = article.body[0]?.processed;
  const nid = article.nid[0]?.value;
  const publicationDate = article.field_publish_date[0]?.value || '';
  const themes = article.field_themes;
  const readTime = Number(article.node_read_time[0]?.value);
  const themeIds = themes.filter((theme) => theme.entity?.name).map((label) => label.target_id);
  const imageStyles = article.field_media_item[0];

  const menu = useSelector<ImmutableRootState<string>, string>((state) => state.getIn(['menu', 'main']));
  const overviewUrl = getOverviewUrlFromMenu(menu, 'articles');

  // Set the block filters.
  const blockFilters: any = { field_article_type_target_id: [articleTypeTargetId] };
  if (themeIds.length > 0) blockFilters.field_themes_target_id = themeIds;
  if (labelsIds.length > 0) blockFilters.field_labels_target_id = labelsIds;

  const { width } = useWindowDimensions();
  const isMobile = width <= SCREEN_TABLET;

  return (
    <article itemType="http://schema.org/WebPage" className="NodeArticlePage">
      <Container>
        <BackButton
          text={t('content_types.article.overview')}
          to={overviewUrl}
        />
        <div className="NodeArticlePage__hero">
          <div className="row">
            <div className="NodeArticlePage__hero__header col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
              <ArticleMetadata>
                <span>
                  {articleType ? (
                    <>{articleType}</>
                  ) : (
                    <>{t('content_types.article.type')}</>
                  )}
                </span>
                <span className="dot">&bull;</span>
                <ArticleMetadata.PublicationDate publicationDate={new Date(publicationDate)} />
                <span className="dot">&bull;</span>
                <ArticleMetadata.ReadTime readTime={readTime} />
              </ArticleMetadata>
              <h1 itemProp="name" className="NodeArticlePage__hero__title">{title}</h1>
              {themes.length > 0 && (
                <ThemesLabels themes={themes} />
              )}
              {body && (
                <WysiwygField
                  property="schema:text"
                  html={body}
                />
              )}
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              {imageStyles && (
                <>
                  <div className="NodeArticlePage__hero__image">
                    <ImageLightbox ariaLabel={t('content_types.article.aria_label')}>
                      <DynamicMediaItem
                        media={imageStyles}
                        imageAspectRatio={isMobile ? '4_3' : '16_9'}
                      />
                    </ImageLightbox>
                  </div>
                  {imageCaption && (
                    <p className="NodeArticlePage__hero__image__caption">{imageCaption}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        {
          paragraphs.length !== 0 ? (
            <div className="row">
              <div className="col-12">
                <div className="NodeArticlePage__paragraphs">
                  {paragraphs.map((paragraph) => {
                    const id = paragraph.target_id;
                    const language = paragraph.entity.langcode[0].value;
                    return (
                      <Paragraph
                        key={`paragraph:${id}:${language}`}
                        entity={Immutable.fromJS(paragraph.entity)}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          ) : null
        }
        <div className="row">
          <div className="col-12 col-md-10 offset-md-1 col-lg-8 offset-lg-2">
            { /* Social Share Block */}
            <ShareWrapper withThemes themes={themes} />
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            {/* Related articles */}
            <Block
              id={BLOCK_RELATED_ARTICLES_BLOCK_ID}
              link={overviewUrl}
              linkTitle={t('content_types.article.overview')}
              viewId="article"
              viewDisplay="related"
              viewFilters={blockFilters}
              viewArgs={[nid]}
            />
          </div>
        </div>
      </Container>
    </article>
  );
};

export default NodeArticlePage;
